import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import RatingStars from "./RatingStars";
import { reviewsAPI } from "../features/reviews/reviewsAPI";
import { useAuthStore } from "../store/authStore";
import Avatar from "./Avatar";

/**
 * Component hiển thị thẻ đánh giá trường học
 * @param {Object} props
 * @param {Object} props.review - Thông tin đánh giá
 * @param {Function} [props.onReviewUpdate] - Callback để cập nhật review sau khi mark helpful
 * @returns {JSX.Element}
 */
export default function ReviewCard({
  review,
  onHelpfulClick,
  onNotHelpfulClick,
  showActions = false,
  onEdit,
  onDelete,
  onReviewUpdate,
}) {
  const [showFullContent, setShowFullContent] = useState(false);
  const [helpfulCounts, setHelpfulCounts] = useState({
    helpful: review.helpful?.length || 0,
    notHelpful: review.notHelpful?.length || 0,
  });
  const [myVote, setMyVote] = useState(null); // null, 'helpful', hoặc 'notHelpful'
  const { isAuthenticated } = useAuthStore();

  // Load vote status khi component mount
  useEffect(() => {
    if (isAuthenticated) {
      loadMyVote();
    }
  }, [isAuthenticated]);

  const loadMyVote = async () => {
    try {
      const result = await reviewsAPI.getMyVote(getReviewId());
      if (result.success) {
        setMyVote(result.data.vote);
        setHelpfulCounts({
          helpful: result.data.helpfulCount,
          notHelpful: result.data.notHelpfulCount,
        });
      }
    } catch (error) {
      console.error("Lỗi khi load vote status:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Xử lý ID để tương thích với backend
  const getSchoolId = () => {
    return review.school?._id || review.school?.id || "";
  };

  const getReviewId = () => {
    return review._id || review.id || "";
  };

  // Xử lý toggle vote
  const handleToggleVote = async (voteType) => {
    if (!isAuthenticated) {
      console.warn("User not authenticated");
      return;
    }

    try {
      let payload;

      // Nếu đã vote loại này rồi thì bỏ vote (gửi object rỗng)
      if (myVote === voteType) {
        payload = {};
      } else {
        // Nếu chưa vote hoặc vote loại khác thì vote loại này
        payload = { helpful: voteType === "helpful" };
      }

      const result = await reviewsAPI.markHelpful(
        getReviewId(),
        payload.helpful
      );

      if (result.success) {
        // Cập nhật trạng thái local
        const newVote = myVote === voteType ? null : voteType;
        setMyVote(newVote);
        setHelpfulCounts({
          helpful: result.data.helpful,
          notHelpful: result.data.notHelpful,
        });

        // Gọi callback để cập nhật review nếu có
        if (onReviewUpdate) {
          onReviewUpdate(getReviewId(), {
            ...review,
            helpful: Array(result.data.helpful).fill(null),
            notHelpful: Array(result.data.notHelpful).fill(null),
          });
        }
      } else {
        console.error("Lỗi toggle vote:", result.error);
      }
    } catch (error) {
      console.error("Lỗi toggle vote:", error);
    }
  };

  // Kiểm tra xem school có tồn tại không
  if (!review.school) {
    return (
      <div className="review-card review-card--error">
        <div className="review-card__error">
          <p>Thông tin trường học không khả dụng</p>
        </div>
      </div>
    );
  }

  return (
    <div className="review-card">
      <div className="review-card__header">
        <div className="review-card__school">
          <Link
            to={`/schools/${getSchoolId()}`}
            className="review-card__school-name"
          >
            {review.school.name || "Tên trường không có"}
          </Link>
          <span className="review-card__school-level">
            {review.school.level || ""}
          </span>
        </div>
        <div className="review-card__rating">
          <RatingStars
            rating={review.overallRating || 0}
            className="review-card__stars"
          />
          <span className="review-card__rating-text">
            {review.overallRating || 0}/5
          </span>
        </div>
      </div>

      <h3 className="review-card__title">
        <Link to={`/reviews/${getReviewId()}`}>
          {review.title || "Không có tiêu đề"}
        </Link>
      </h3>

      <div className="review-card__content">
        {showFullContent
          ? review.content
          : `${review.content?.substring(0, 200) || ""}...`}
        {review.content && review.content.length > 200 && (
          <button
            onClick={() => setShowFullContent(!showFullContent)}
            className="review-card__read-more"
          >
            {showFullContent ? "Thu gọn" : "Xem thêm"}
          </button>
        )}
      </div>

      <div className="review-card__details">
        <div className="review-card__categories">
          <div className="category-rating">
            <span>Giảng dạy:</span>
            <RatingStars
              rating={review.ratings?.teaching || 0}
              integerOnly={true}
            />
          </div>
          <div className="category-rating">
            <span>Cơ sở vật chất:</span>
            <RatingStars
              rating={review.ratings?.facilities || 0}
              integerOnly={true}
            />
          </div>
          <div className="category-rating">
            <span>Hoạt động:</span>
            <RatingStars
              rating={review.ratings?.activities || 0}
              integerOnly={true}
            />
          </div>
          <div className="category-rating">
            <span>Môi trường:</span>
            <RatingStars
              rating={review.ratings?.environment || 0}
              integerOnly={true}
            />
          </div>
        </div>
      </div>

      <div className="review-card__footer">
        <div className="review-card__author">
          <Avatar
            user={review.user}
            size="sm"
            className="review-card__avatar"
          />
          <div className="review-card__author-info">
            <span className="review-card__author-name">
              {review.user?.name || "Người dùng"}
            </span>
            <span className="review-card__date">
              {formatDate(review.createdAt)}
            </span>
          </div>
        </div>

        <div className="review-card__actions">
          <div className="review-card__helpful">
            <button
              className={`btn btn--icon ${
                myVote === "helpful" ? "btn--icon--active-helpful" : ""
              }`}
              onClick={() => handleToggleVote("helpful")}
              disabled={!isAuthenticated}
            >
              👍 {helpfulCounts.helpful}
            </button>
            <button
              className={`btn btn--icon ${
                myVote === "notHelpful" ? "btn--icon--active-not-helpful" : ""
              }`}
              onClick={() => handleToggleVote("notHelpful")}
              disabled={!isAuthenticated}
            >
              👎 {helpfulCounts.notHelpful}
            </button>
          </div>
          <span className="review-card__comments">
            💬 {review.commentCount || 0}
          </span>
        </div>
      </div>
    </div>
  );
}
